<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30">

      <!-- Loader -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-2 text-muted">Chargement des questions...</p>
      </div>

      <template v-else-if="composition">

        <!-- Avertissement anti-navigation -->
        <div class="alert alert-danger d-flex align-items-center mb-20">
          <i class="fa fa-exclamation-triangle me-10 fs-20"></i>
          <div>
            <strong>Attention !</strong> Une fois le quiz démarré, vous ne pouvez pas quitter cette page.
            Si vous fermez votre navigateur ou revenez en arrière, cette tentative sera perdue.
          </div>
        </div>

        <!-- Barre supérieure sticky : titre + timer + essais -->
        <div class="quiz-header d-flex align-items-center justify-content-between mb-25 p-15 rounded-2 border"
          :class="timerClass"
          style="position: sticky; top: 70px; z-index: 100; background: white;">
          <div>
            <h5 class="fw-bold mb-0">{{ composition.titre }}</h5>
            <small class="text-muted">{{ composition.filiereNiveauMatiere?.matiere?.libelle }}</small>
            
            <div class="mt-2">
              <span class="badge bg-warning fs-12">
                Essai {{ composition.tentativesEffectuees + 1 }} / {{ composition.nombreEssaisAutorise }}
              </span>
              <span v-if="composition.tentativesRestantes > 1" class="text-muted ms-2 small">
                ({{ composition.tentativesRestantes }} restants)
              </span>
              <span v-else-if="composition.tentativesRestantes === 1" class="text-muted ms-2 small">
                ({{ composition.tentativesRestantes }} restant)
              </span>
              <span v-else class="text-danger ms-2 fw-bold small">
                 Dernier essai - Seule cette note sera retenue
              </span>
            </div>
          </div>
          <div class="text-center">
            <div class="fw-bold fs-22" :class="timerTextClass">
              <i class="fa fa-clock-o me-5"></i>{{ timerDisplay }}
            </div>
            <small class="text-muted">Temps restant</small>
          </div>
          <div class="text-end">
            <span class="badge bg-info fs-13">
              {{ nbRepondu }} / {{ questions.length }} répondues
            </span>
          </div>
        </div>

        <!-- Progression -->
        <div class="progress mb-25" style="height: 8px;">
          <div
            class="progress-bar bg-success"
            :style="{ width: progressPercent + '%' }"
            role="progressbar"
          ></div>
        </div>

        <!-- Questions -->
        <div v-for="(compoQ, index) in questions" :key="compoQ.question.id" class="mb-25">
          <div class="border rounded-2 p-20"
            :class="estQuestionRepondue(compoQ.question.id) ? 'border-success' : 'border-light'">

            <!-- Numéro + contenu -->
            <div class="d-flex align-items-start gap-10 mb-15">
              <span class="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
                style="min-width:30px; height:30px; font-size:14px;">
                {{ index + 1 }}
              </span>
              <p class="fw-medium mb-0 fs-15">{{ compoQ.question.contenu }}</p>
            </div>

            <!-- Propositions OU saisie libre -->
            <div class="ps-40">
              <!-- Cas QCM : propositions à cocher -->
              <div v-if="compoQ.question.propositions && compoQ.question.propositions.length > 0">
                <div
                  v-for="proposition in compoQ.question.propositions"
                  :key="proposition.id"
                  class="form-check mb-10 p-10 rounded-2 cursor-pointer"
                  :class="getPropositionClass(compoQ.question.id, proposition.id)"
                  @click="choisirReponse(compoQ.question.id, proposition.id, compoQ.question.type)"
                >
                  <input
                    v-if="compoQ.question.type === 'QCM'"
                    class="form-check-input"
                  
                    :checked="isChecked(compoQ.question.id, proposition.id)"
                    @change="choisirReponse(compoQ.question.id, proposition.id, compoQ.question.type)"
                    :id="`q${compoQ.question.id}_p${proposition.id}`"
                  />
                  <input
                    v-else
                    class="form-check-input"
                    type="radio"
                    :name="`question_${compoQ.question.id}`"
                    :checked="isChecked(compoQ.question.id, proposition.id)"
                    @change="choisirReponse(compoQ.question.id, proposition.id, compoQ.question.type)"
                    :id="`q${compoQ.question.id}_p${proposition.id}`"
                  />
                  <label
                    class="form-check-label fw-medium cursor-pointer w-100"
                    :for="`q${compoQ.question.id}_p${proposition.id}`"
                  >
                    {{ proposition.contenu }}
                  </label>
                </div>
              </div>

              <!-- Cas Questions/Réponses : saisie libre -->
              <div v-else>
                <textarea
                  class="form-control shadow-none"
                  rows="5"
                  placeholder="Rédigez votre réponse ici..."
                  :value="reponsesTexte.get(compoQ.question.id) || ''"
                  @input="saisirReponseTexte(compoQ.question.id, ($event.target as HTMLTextAreaElement).value)"
                ></textarea>
              </div>
            </div>

          </div>
        </div>

        <!-- Bouton soumettre -->
        <div class="text-center mt-30">
          <button
            class="btn btn-success btn-lg px-40 fw-bold"
            @click="confirmerSoumission"
            :disabled="soumissionEnCours"
          >
            <span v-if="soumissionEnCours">
              <span class="spinner-border spinner-border-sm me-5"></span>
              Soumission en cours...
            </span>
            <span v-else>
              <i class="fa fa-paper-plane me-8"></i>
              Soumettre mes réponses
            </span>
          </button>
        </div>

      </template>

    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";
import ApiService from "@/services/ApiService";
import { error } from "@/utils/utils";


export default defineComponent({
  name: "ComposerQuizPage",
  setup() {
    const route = useRoute();
    const router = useRouter();

    const composition = ref<any>(null);
    const questions = ref<any[]>([]);
    const loading = ref(true);
    const soumissionEnCours = ref(false);
    const quizDemarre = ref(false); // Pour savoir si le quiz a commencé

    // Map<questionId, Set<propositionId>> pour QCM
    const reponsesChoisies = ref<Map<number, Set<number>>>(new Map());

    // Map<questionId, string> pour QuestionsReponses (texte libre)
    const reponsesTexte = ref<Map<number, string>>(new Map());

    // Timer
    const secondesRestantes = ref(0);
    let timerInterval: ReturnType<typeof setInterval> | null = null;

    // Protection anti-navigation
    const bloquerNavigation = (e: BeforeUnloadEvent) => {
      if (quizDemarre.value && !soumissionEnCours.value) {
        e.preventDefault();
        e.returnValue = 'Êtes-vous sûr de vouloir quitter ? Vos réponses seront perdues.';
        return e.returnValue;
      }
    };

    // Bloquer le bouton retour du navigateur
    const empecherRetourArriere = () => {
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', gererRetourArriere);
    };

    const gererRetourArriere = async () => {
      if (quizDemarre.value && !soumissionEnCours.value) {
        window.history.pushState(null, '', window.location.href);
        
        const result = await Swal.fire({
          title: 'Attention !',
          html: '<p>Vous ne pouvez pas quitter la composition en cours.</p><p>Si vous quittez, cette tentative sera perdue.</p>',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Abandonner la composition',
          cancelButtonText: 'Continuer la composition',
          confirmButtonColor: '#dc3545',
          cancelButtonColor: '#28a745',
        });

        if (result.isConfirmed) {
          quizDemarre.value = false;
          window.removeEventListener('beforeunload', bloquerNavigation);
          window.removeEventListener('popstate', gererRetourArriere);
          router.push({ name: 'ListeCompositionPage' });
        }
      }
    };

    onMounted(async () => {
      await fetchComposition();
      if (composition.value) {
        const duree = composition.value.dureeMinutes || 45;
        secondesRestantes.value = duree * 60;
        quizDemarre.value = true;
        
        // Activer les protections
        window.addEventListener('beforeunload', bloquerNavigation);
        empecherRetourArriere();
        
        demarrerTimer();
      }
    });

    onUnmounted(() => {
      if (timerInterval) clearInterval(timerInterval);
      window.removeEventListener('beforeunload', bloquerNavigation);
      window.removeEventListener('popstate', gererRetourArriere);
    });

    async function fetchComposition() {
      try {
        const { data } = await ApiService.get(`/compositions/${route.params.id}/mon-devoir`);
        
        if (!data.data.peutEncoreComposer) {
          await Swal.fire({
            title: 'Essais épuisés',
            text: `Vous avez déjà effectué ${data.data.tentativesEffectuees} essai(s). Vous ne pouvez plus recommencer.`,
            icon: 'warning',
          });
          router.push({ name: 'ComposerDetailPage', params: { id: route.params.id } });
          return;
        }

        composition.value = data.data;
        questions.value = data.data.compoQuestions || [];
      } catch (e: any) {
        error(e?.response?.data?.message || "Erreur lors du chargement");
      } finally {
        loading.value = false;
      }
    }

    // ─── Timer ───────────────────────────────────────────────
    function demarrerTimer() {
      timerInterval = setInterval(() => {
        if (secondesRestantes.value <= 1) {
          clearInterval(timerInterval!);
          secondesRestantes.value = 0;
          soumettreAutomatiquement();
        } else {
          secondesRestantes.value--;
        }
      }, 1000);
    }

    const timerDisplay = computed(() => {
      const m = Math.floor(secondesRestantes.value / 60);
      const s = secondesRestantes.value % 60;
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    });

    const timerClass = computed(() => {
      if (secondesRestantes.value <= 60) return 'border-danger bg-danger bg-opacity-10';
      if (secondesRestantes.value <= 300) return 'border-warning bg-warning bg-opacity-10';
      return 'border-light';
    });

    const timerTextClass = computed(() => {
      if (secondesRestantes.value <= 60) return 'text-danger';
      if (secondesRestantes.value <= 300) return 'text-warning';
      return 'text-success';
    });

    // ─── Réponses QCM ─────────────────────────────────────────
    function choisirReponse(questionId: number, propositionId: number, type: string) {
      const map = reponsesChoisies.value;

      if (type === 'QCM_MULTIPLE') {
        if (!map.has(questionId)) map.set(questionId, new Set());
        const set = map.get(questionId)!;
        if (set.has(propositionId)) {
          set.delete(propositionId);
          if (set.size === 0) map.delete(questionId);
        } else {
          set.add(propositionId);
        }
      } else {
        map.set(questionId, new Set([propositionId]));
      }

      reponsesChoisies.value = new Map(map);
    }

    function isChecked(questionId: number, propositionId: number): boolean {
      return reponsesChoisies.value.get(questionId)?.has(propositionId) ?? false;
    }

    function getPropositionClass(questionId: number, propositionId: number): string {
      if (isChecked(questionId, propositionId)) {
        return 'bg-primary bg-opacity-10 border border-primary';
      }
      return 'bg-light border border-transparent';
    }

    // ─── Réponses texte libre ───────────────────────────────
    function saisirReponseTexte(questionId: number, valeur: string) {
      reponsesTexte.value.set(questionId, valeur);
      reponsesTexte.value = new Map(reponsesTexte.value); // réactivité
    }

    // ─── Suivi global (QCM + texte confondus) ───────────────
    function estQuestionRepondue(questionId: number): boolean {
      if (reponsesChoisies.value.has(questionId)) return true;
      const texte = reponsesTexte.value.get(questionId);
      return !!texte && texte.trim().length > 0;
    }

    const nbRepondu = computed(() => {
      return questions.value.filter((compoQ: any) => estQuestionRepondue(compoQ.question.id)).length;
    });

    const progressPercent = computed(() => {
      if (!questions.value.length) return 0;
      return Math.round((nbRepondu.value / questions.value.length) * 100);
    });

    // ─── Soumission ───────────────────────────────────────────
    async function confirmerSoumission() {
      const questionsNonRepondues = questions.value.length - nbRepondu.value;

      const result = await Swal.fire({
        title: 'Confirmer la soumission',
        html: questionsNonRepondues > 0
          ? `<p>Vous avez <strong class="text-danger">${questionsNonRepondues} question(s) sans réponse</strong>.</p><p>Voulez-vous quand même soumettre ?</p>`
          : `<p>Vous avez répondu à toutes les questions.</p><p>Voulez-vous soumettre vos réponses ?</p>`,
        icon: questionsNonRepondues > 0 ? 'warning' : 'question',
        showCancelButton: true,
        confirmButtonText: 'Oui, soumettre',
        cancelButtonText: 'Continuer',
        confirmButtonColor: '#28a745',
      });

      if (result.isConfirmed) {
        await soumettre();
      }
    }

    async function soumettreAutomatiquement() {
      await Swal.fire({
        title: '⏰ Temps écoulé !',
        text: 'Le temps imparti est terminé. Vos réponses vont être soumises automatiquement.',
        icon: 'warning',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
        allowOutsideClick: false,
      });
      await soumettre();
    }

    async function soumettre() {
      if (soumissionEnCours.value) return;
      soumissionEnCours.value = true;

      if (timerInterval) clearInterval(timerInterval);

      const reponses: { questionId: number; propositionIds: number[]; contenu?: string }[] = [];

      // Réponses QCM (propositions cochées)
      reponsesChoisies.value.forEach((propositions, questionId) => {
        reponses.push({
          questionId,
          propositionIds: Array.from(propositions),
        });
      });

      // Réponses texte libre (QuestionsReponses)
      reponsesTexte.value.forEach((contenu, questionId) => {
        if (contenu.trim()) {
          reponses.push({
            questionId,
            propositionIds: [],
            contenu: contenu.trim(),
          });
        }
      });

      try {
        const { data } = await ApiService.post(
          `/compositions/${route.params.id}/soumettre`,
          { reponses }
        );

        // Désactiver les protections avant la redirection
        quizDemarre.value = false;
        window.removeEventListener('beforeunload', bloquerNavigation);
        window.removeEventListener('popstate', gererRetourArriere);

        router.push({
          name: 'ResultatCompositionPage',
          params: { id: route.params.id },
        })
      } catch (e: any) {
        soumissionEnCours.value = false;
        error(e?.response?.data?.message || "Erreur lors de la soumission");
      }
    }

    return {
      composition,
      questions,
      loading,
      soumissionEnCours,
      reponsesChoisies,
      reponsesTexte,
      nbRepondu,
      timerDisplay,
      timerClass,
      timerTextClass,
      progressPercent,
      choisirReponse,
      saisirReponseTexte,
      isChecked,
      getPropositionClass,
      estQuestionRepondue,
      confirmerSoumission,
    };
  },
});
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
  transition: all 0.15s ease;
}
.cursor-pointer:hover {
  opacity: 0.85;
}
.quiz-header {
  box-shadow: 0 2px 10px rgba(0,0,0,0.07);
  transition: all 0.3s ease;
}
</style>